import { useState } from "react";
import { motion } from "framer-motion";

export const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Resolve an image value that is either an absolute URL or a stored CMS media path
export function siteImg(val) {
  if (!val) return "";
  return /^https?:\/\//.test(val) ? val : `${API}/site/media/${val}`;
}

export function Container({ className = "", children }) {
  return <div className={`mx-auto w-full max-w-[1200px] px-6 sm:px-8 ${className}`}>{children}</div>;
}

export function Reveal({ children, delay = 0, y = 24, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export function Eyebrow({ children }) {
  return (
    <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--rgh-primary)]">
      <span className="h-px w-6 bg-[var(--rgh-primary)]" />
      {children}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, sub, center = false, dark = false }) {
  return (
    <div className={`${center ? "text-center mx-auto max-w-2xl" : "max-w-2xl"}`}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className={`mt-4 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight ${dark ? "text-white" : "text-[#0A0A0A]"}`}>{title}</h2>
      {sub && <p className={`mt-4 text-base sm:text-lg leading-relaxed ${dark ? "text-white/70" : "text-[#525252]"}`}>{sub}</p>}
    </div>
  );
}

export function PageBanner({ image, eyebrow, title, subtitle }) {
  return (
    <section className="relative pt-[192px]" data-testid="page-banner">
      <div className="relative h-[300px] md:h-[380px] overflow-hidden">
        <img src={siteImg(image)} alt={title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/20" />
        <Container className="relative h-full flex flex-col justify-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            {eyebrow && <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white"><span className="h-px w-6 bg-[var(--rgh-primary)]" />{eyebrow}</span>}
            <h1 className="mt-4 max-w-3xl text-4xl sm:text-5xl md:text-6xl font-bold tracking-[-0.02em] leading-[1.02] text-white">{title}</h1>
            {subtitle && <p className="mt-4 max-w-2xl text-base sm:text-lg leading-relaxed text-white/80">{subtitle}</p>}
          </motion.div>
        </Container>
      </div>
    </section>
  );
}

export function FAQ({ items = [] }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="divide-y divide-black/10 border-y border-black/10" data-testid="faq">
      {items.map((it, i) => (
        <div key={i} data-testid={`faq-item-${i}`}>
          <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-5 text-left">
            <span className="text-base sm:text-lg font-semibold text-[#0A0A0A]">{it.q}</span>
            <span className={`shrink-0 transition-transform duration-300 ${open === i ? "rotate-45" : ""} text-[var(--rgh-primary)] text-2xl leading-none`}>+</span>
          </button>
          <div className={`grid transition-all duration-300 ${open === i ? "grid-rows-[1fr] pb-5" : "grid-rows-[0fr]"}`}>
            <p className="overflow-hidden text-[#525252] leading-relaxed">{it.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
